import { type ComponentPropsWithoutRef, useMemo, useState } from 'react';
import classNames from 'classnames';
import type { Departamento } from '@/interfaces/strapi';

type DepartamentoItemProps = Omit<ComponentPropsWithoutRef<'article'>, 'title'> & {
	readonly item: Departamento;
	readonly className?: string;
};
const classNameComponent = 'c-cnm-departamento';

function toEmbedUrl(mapUrl: string | undefined, address: string | undefined): string | null {
	// Preferimos un URL válido de embed
	const isEmbedLike = (u: URL) =>
		u.hostname.includes('google') &&
		u.pathname.includes('maps') &&
		(u.pathname.includes('/embed') ?? u.searchParams.has('output'));

	// 1) Si viene una URL válida de Google Maps, la adaptamos
	if (mapUrl) {
		try {
			const u = new URL(mapUrl);
			if (isEmbedLike(u)) {
				// Ya es embebible
				return u.toString();
			}
			// Si es un maps "normal" (place, dir, etc.), forzamos output=embed en www.google.com
			if (u.hostname.includes('google') && u.pathname.includes('maps')) {
				const forced = new URL('https://www.google.com/maps');
				// Si ya trae q, la respetamos; si no, intentamos con la address como fallback
				const q = u.searchParams.get('q') ?? address ?? '';
				if (!q) return null;
				forced.searchParams.set('q', q);
				forced.searchParams.set('output', 'embed');
				return forced.toString();
			}
			// Shorteners como maps.app.goo.gl no se pueden embeber -> pasamos a fallback
		} catch {}
	}

	// 2) Fallback: si hay address, montamos un embed con q=address
	if (address) {
		const forced = new URL('https://www.google.com/maps');
		forced.searchParams.set('q', address);
		forced.searchParams.set('output', 'embed');
		return forced.toString();
	}

	return null;
}

export const DepartamentoItem = ({ item, className, ...rest }: DepartamentoItemProps) => {
	const wrapperClassName = useMemo(() => classNames(classNameComponent, className), [className]);
	const [open, setOpen] = useState(false);
	const panelId = `map-panel-${item.id}`;
	const embedSrc = toEmbedUrl(item.map, item.address);

	return (
		<article className={wrapperClassName} {...rest}>
			<header className={`${classNameComponent}__header`}>
				<h3 className={`${classNameComponent}__title`}>{item.name}</h3>

				{embedSrc ? (
					<button
						type="button"
						className={`${classNameComponent}__map-toggle`}
						aria-expanded={open}
						aria-controls={panelId}
						onClick={() => setOpen((v) => !v)}
					>
						{open ? 'Ocultar mapa' : 'Ver mapa'}
					</button>
				) : null}
			</header>

			<div
				id={panelId}
				role="region"
				aria-label={`Mapa de ${item.name}`}
				aria-hidden={!open}
				className={classNames(`${classNameComponent}__accordion`, { 'is-open': open })}
			>
				{open && embedSrc ? (
					<div className={`${classNameComponent}__map-wrapper`}>
						<iframe
							className={`${classNameComponent}__map-iframe`}
							src={embedSrc}
							title={`Mapa de ${item.name}`}
							loading="lazy"
							referrerPolicy="no-referrer-when-downgrade"
							sandbox="allow-scripts"
						/>
					</div>
				) : null}
			</div>

			<div className={`${classNameComponent}__meta`}>
				{item.address && (
					<p className={`${classNameComponent}__line`}>
						<strong>Dirección:</strong> {item.address}
					</p>
				)}
				{item.phone && (
					<p className={`${classNameComponent}__line`}>
						<strong>Teléfono:</strong> {item.phone}
					</p>
				)}
				{/* Soporte opcional por si en algún caso llega fax aunque no esté tipado */}
				{(item as any)?.fax && (
					<p className={`${classNameComponent}__line`}>
						<strong>Fax:</strong> {(item as any).fax}
					</p>
				)}
			</div>
		</article>
	);
};
